import React, { useState, useRef, useEffect } from "react";
import { getSentencePool } from "../EnglishTool/pool/sentencePool";
import { shuffleArray } from "../EnglishTool/shuffleArray";
import "./EnglishSentenceUnscramble.css";
import { awardToken } from "../../awardtoken";
import { db } from "../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth";

const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

const TOOL_TYPE = "EnglishSentenceUnscramblePractice";

async function logUnscramblePractice({ user, grade, score, attempts, startTime, endTime }) {
  if (!user?.uid) {
    console.warn("No user UID! User:", user);
    return;
  }
  if (attempts === 0) {
    console.warn("No attempts, nothing to log.");
    return;
  }
  try {
    await setDoc(
      doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
      {
        toolType: TOOL_TYPE,
        topic: `sentence unscramble (grade ${grade})`,
        numQuestions: attempts,
        correct: score,
        attempts: attempts,
        timestamp: serverTimestamp(),
        startedAt: startTime,
        finishedAt: endTime,
        durationSec: Math.round((endTime - startTime) / 1000),
      },
      { merge: true }
    );
    console.log("Unscramble session log successfully written to Firestore.");
  } catch (e) {
    console.error("Error writing unscramble session log:", e);
  }
}

export default function EnglishSentenceUnscramble({ onAwardTokens }) {
  const [grade, setGrade] = useState(3);
  const sentencePool = getSentencePool(grade);
  const [sentence, setSentence] = useState(() => randomSentence());
  const [tokens, setTokens] = useState(() => shuffleArray(sentence.split(" ")));
  const [chosen, setChosen] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const tokensAwarded = useRef(0);
  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());
  const sessionStats = useRef({ score: 0, attempts: 0 });
  const didMountRef = useRef(false);

  useEffect(() => {
  if (!didMountRef.current) {
    didMountRef.current = true;
    return;
  }
  const endTime = Date.now();
  if (sessionStats.current.attempts > 0) {
    logUnscramblePractice({
      user,
      grade,
      score: sessionStats.current.score,
      attempts: sessionStats.current.attempts,
      startTime: sessionStart,
      endTime,
    });
  }
  setSessionStart(Date.now());
  setScore(0);
  setAttempts(0);
  sessionStats.current = { score: 0, attempts: 0 };
  tokensAwarded.current = 0;
  // eslint-disable-next-line
}, [grade]);

useEffect(() => {
  return () => {
    const endTime = Date.now();
    if (sessionStats.current.attempts > 0) {
      logUnscramblePractice({
        user,
        grade,
        score: sessionStats.current.score,
        attempts: sessionStats.current.attempts,
        startTime: sessionStart,
        endTime,
      });
    }
  };
  // eslint-disable-next-line
}, []);


  function randomSentence() {
    if (!sentencePool || sentencePool.length === 0) return "No sentence";
    return sentencePool[Math.floor(Math.random() * sentencePool.length)].sentence;
  }

  function handleGradeChange(e) {
    const newGrade = parseInt(e.target.value, 10);
    setGrade(newGrade);
    const newPool = getSentencePool(newGrade);
    const newSentence =
      newPool && newPool.length > 0
        ? newPool[Math.floor(Math.random() * newPool.length)].sentence
        : "No sentence";
    setSentence(newSentence);
    setTokens(shuffleArray(newSentence.split(" ")));
    setChosen([]);
    setFeedback(null);
    setAttempts(0);
    setScore(0);
    tokensAwarded.current = 0;
  }

  function onDragStart(e, idx, from) {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, from }));
  }

function onDropToChosen(e) {
  e.preventDefault();
  const data = e.dataTransfer.getData("text/plain");
  if (!data) return; // 只有拖曳才會有 data
  const { idx, from } = JSON.parse(data);
  if (from === "tokens") {
    setChosen([...chosen, tokens[idx]]);
    setTokens(tokens.filter((_, i) => i !== idx));
  }
}

function onDropToTokens(e) {
  e.preventDefault();
  const data = e.dataTransfer.getData("text/plain");
  if (!data) return; // 只有拖曳才會有 data
  const { idx, from } = JSON.parse(data);
  if (from === "chosen") {
    setTokens([...tokens, chosen[idx]]);
    setChosen(chosen.filter((_, i) => i !== idx));
  }
}

function check() {
  const userSentence = chosen.join(" ").replace(/\s+/g, " ").trim();
  const nextAttempts = attempts + 1;
  const isCorrect = userSentence === sentence;
  const nextScore = isCorrect ? score + 1 : score;

  setAttempts(nextAttempts);
  setScore(nextScore);
  sessionStats.current = { score: nextScore, attempts: nextAttempts };

  // Log every answer to Firestore
  const endTime = Date.now();
  logUnscramblePractice({
    user,
    grade,
    score: nextScore,
    attempts: nextAttempts,
    startTime: sessionStart,
    endTime,
  });

  if (isCorrect) {
    setFeedback("✅ Correct!");
    // Tokens
    if (nextScore >= (tokensAwarded.current + 1) * 10) {
      tokensAwarded.current += 1;
      awardToken();
      if (onAwardTokens) onAwardTokens(1);
    }
    setTimeout(() => {
      const s = randomSentence();
      setSentence(s);
      setTokens(shuffleArray(s.split(" ")));
      setChosen([]);
      setFeedback(null);
    }, 1200);
  } else {
    if (attempts >= 1) {
      setFeedback(`❌ Incorrect. The correct sentence is: "${sentence}"`);
      setTimeout(() => {
        const s = randomSentence();
        setSentence(s);
        setTokens(shuffleArray(s.split(" ")));
        setChosen([]);
        setFeedback(null);
      }, 2000);
    } else {
      setFeedback("❌ Try again!");
      setAttempts(attempts + 1);
    }
  }
}


  function reset() {
    setTokens(shuffleArray(sentence.split(" ")));
    setChosen([]);
    setFeedback(null);
    tokensAwarded.current = 0;
  }

  if (!sentencePool || sentencePool.length === 0 || sentence === "No sentence") {
    return (
      <div className="english-practice-card">
        <h2>Sentence Unscramble</h2>
        <div>
          <label>
            Grade:{" "}
            <select value={grade} onChange={handleGradeChange}>
              {GRADE_OPTIONS.map((g) => (
                <option key={g} value={g}>
                  Grade {g}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="unscramble-no-sentence">
          No sentences found for this grade.<br />
          Please select another grade or contact admin.
        </div>
      </div>
    );
  }

  return (
    <div className="english-practice-card">
      <h2>Sentence Unscramble</h2>
      <div>
        <label>
          Grade:{" "}
          <select value={grade} onChange={handleGradeChange}>
            {GRADE_OPTIONS.map((g) => (
              <option key={g} value={g}>
                Grade {g}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="unscramble-instruction">
        Drag the words into the correct order.
      </div>
      <div
        className="unscramble-tokens-area"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropToTokens}
      >
        {tokens.map((w, i) => (
          <span
            key={i}
            className="unscramble-token"
            draggable
            onDragStart={e => onDragStart(e, i, "tokens")}
          >
            {w}
          </span>
        ))}
        <span className="unscramble-hint">← Drag these words below</span>
      </div>

      <div
  className="unscramble-chosen-area"
  onDragOver={e => e.preventDefault()}
  onDrop={onDropToChosen}
>
  {chosen.length === 0
    ? <span className="unscramble-placeholder" style={{ pointerEvents: 'none' }}>Drag here to build your sentence.</span>
    : chosen.map((w, i) => (
      <span
        key={i}
        className="unscramble-chosen-token"
        draggable
        onDragStart={e => onDragStart(e, i, "chosen")}
      >{w}</span>
    ))}
</div>

      <div className="unscramble-sentence">
        <b>Your Sentence: </b>
        {chosen.join(" ")}
      </div>
      <button onClick={check} disabled={chosen.length === 0}>
        Check
      </button>
      <button onClick={reset} style={{ marginLeft: 8 }}>
        Reset
      </button>
      {feedback && <div style={{ marginTop: 10 }}>{feedback}</div>}
      <div style={{ marginTop: 10 }}>Score: {score}</div>
      <button
  className="math-pill-btn"
  style={{ margin: "1em 0" }}
  onClick={() => {
    const endTime = Date.now();
    if (sessionStats.current.attempts > 0) {
      logUnscramblePractice({
        user,
        grade,
        score: sessionStats.current.score,
        attempts: sessionStats.current.attempts,
        startTime: sessionStart,
        endTime,
      });
      setSessionStart(Date.now());
      setScore(0);
      setAttempts(0);
      sessionStats.current = { score: 0, attempts: 0 };
      tokensAwarded.current = 0;
    }
  }}
>
  Finish Session
</button>

    </div>
  );
}
